// Access Admin — Phase 11.4.
//
// Admin-only matrix for managing per-user Command Center app
// visibility. Rows = active staff; columns = catalog apps; cells are
// 3-state toggles:
//   ⊕  override=true  — force-show
//   ⊖  override=false — force-hide
//   ·  override=null  — default (admin tiles → admin only; rest → all)
//
// One toggle = one PATCH /admin/app-access call. Optimistic-style cache
// invalidation; the matrix re-renders on every successful set.
//
// Pattern follows CategoriesAdminPage / ColorsAdminPage — TanStack
// Query for fetch, useMutation for write, brand-locked palette, no
// React Hook Form. Light-mode (Workflow shell) since this surface
// lives under /admin/* alongside Categories + Colors, not under
// /command/*.

import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Check, Minus, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ROLE_CHOICES, STAFF_ROLE_OPTIONS, roleChoicesOf, roleWords, rolesFromChoices } from "@/lib/roles";

// Staff role vocabulary — mirrors STAFF_ROLES in apps/api/src/services/auth.ts
// (incl. Marketing, CEO 2026-10-21). Lives in lib/roles.ts; the offline gate
// qa-marketing-foundation checks the two lists agree.
const STAFF_ROLES = STAFF_ROLE_OPTIONS;

interface MatrixUser {
  id: string;
  email: string;
  fullName: string | null;
  roles: string[];
}

interface MatrixApp {
  slug: string;
  name: string;
  monogram: string;
  status: "live" | "soon" | "coming_soon";
  badgeTone: "peach" | "rust" | "cream" | "brown";
}

interface MatrixCell {
  userId: string;
  appSlug: string;
  effective: boolean;
  override: boolean | null;
  defaultVisible: boolean;
}

interface MatrixResponse {
  users: MatrixUser[];
  apps: MatrixApp[];
  cells: MatrixCell[];
}

const BADGE_BG: Record<MatrixApp["badgeTone"], string> = {
  peach: "bg-peach-400 text-white",
  rust: "bg-ink text-cream-50",
  cream: "bg-cream-200 text-brown-500",
  brown: "bg-brown-300 text-cream-50",
};

export function AccessAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;
  const queryClient = useQueryClient();
  const matrixKey = useMemo(() => ["admin", "app-access"] as const, []);

  const matrixQ = useQuery({
    queryKey: matrixKey,
    queryFn: () => api<MatrixResponse>("/admin/app-access"),
    enabled: isAdmin,
  });

  const [toggleError, setToggleError] = useState<string | null>(null);

  const setOverride = useMutation({
    mutationFn: async (args: {
      userId: string;
      appSlug: string;
      visible: boolean | null;
    }) =>
      api(`/admin/app-access`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: args.userId,
          app_slug: args.appSlug,
          visible: args.visible,
        }),
      }),
    onSuccess: () => {
      setToggleError(null);
      void queryClient.invalidateQueries({ queryKey: matrixKey });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setToggleError(body?.message ?? body?.error ?? `Toggle failed (${err.status}).`);
      } else {
        setToggleError("Toggle failed. Try again.");
      }
    },
  });
  const data = matrixQ.data;
  const cellsByKey = useMemo(() => {
    const m = new Map<string, MatrixCell>();
    for (const c of data?.cells ?? []) {
      m.set(`${c.userId}::${c.appSlug}`, c);
    }
    return m;
  }, [data]);

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Access
        </h1>
        <p className="mt-3 text-sm text-brown-300">
          This admin surface is only visible to users with the admin role.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full space-y-8">
      {/* Header */}
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Admin
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Access
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-brown-400">
          Control which Command Center apps each staff member sees on
          their dashboard. A light tick means their roles already show it.
          Click a box to always show it (black tick), again to always hide
          it (red ✕), and again to go back to what their roles give.
        </p>
      </div>

      {/* Invite teammate — self-serve onboarding (2026-07-20) */}
      <InviteTeammate />

      {/* Two-step sign-in — who has it on, reset (2026-10-21) */}
      <TwoStepAdmin />

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-brown-100 bg-cream-50/60 px-4 py-3 text-xs text-brown-400">
        <span className="font-semibold uppercase tracking-wide">Legend</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-peach-400 text-white">
            <Check className="h-3 w-3" aria-hidden />
          </span>
          Force show
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-rust-100 text-rust-600">
            <X className="h-3 w-3" aria-hidden />
          </span>
          Force hide
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-md border border-brown-200 bg-cream-100 text-brown-400">
            <Check className="h-3 w-3" aria-hidden />
          </span>
          Shown by their roles
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-md border border-brown-100 text-brown-300">
            <Minus className="h-3 w-3" aria-hidden />
          </span>
          Hidden by their roles
        </span>
      </div>

      {matrixQ.isLoading && (
        <p className="text-sm text-brown-300">Loading matrix…</p>
      )}
      {matrixQ.isError && (
        <p className="text-sm text-rust-600">Could not load the access matrix.</p>
      )}
      {toggleError && (
        <div className="rounded-md border border-rust-300 bg-rust-50 px-4 py-2 text-sm text-rust-600">
          {toggleError}
        </div>
      )}

      {data && (
        <div className="overflow-x-auto rounded-lg border border-brown-100 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="sticky left-0 z-10 bg-cream-50/60 px-4 py-3 text-left font-semibold text-brown-300">
                  Staff member
                </th>
                {data.apps.map((app) => (
                  <th
                    key={app.slug}
                    className="px-3 py-3 text-center font-semibold text-brown-300"
                    title={app.name}
                  >
                    <div className="flex flex-col items-center gap-1.5">
                      <span
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-md font-display text-xs font-semibold",
                          BADGE_BG[app.badgeTone],
                        )}
                        aria-hidden
                      >
                        {app.monogram}
                      </span>
                      <span className="whitespace-nowrap text-[11px] font-semibold text-brown-400">
                        {app.name}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id} className="border-b border-brown-50 last:border-0">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 align-middle">
                    <div className="font-semibold text-brown-500">
                      {u.fullName ?? u.email}
                    </div>
                    <div className="text-[11px] text-brown-300">
                      {roleWords(u.roles) || "no roles"}
                    </div>
                    {u.roles.some((r) => STAFF_ROLES.some((s) => s.value === r)) &&
                      !u.roles.some((r) => r === "client" || r === "store_member") && (
                        <RolesEditor userId={u.id} roles={u.roles} />
                      )}
                  </td>
                  {data.apps.map((app) => {
                    const cell = cellsByKey.get(`${u.id}::${app.slug}`);
                    if (!cell) return <td key={app.slug} />;
                    return (
                      <td
                        key={app.slug}
                        className="px-3 py-2 text-center align-middle"
                      >
                        <CellToggle
                          cell={cell}
                          disabled={setOverride.isPending}
                          onChange={(next) =>
                            setOverride.mutate({
                              userId: u.id,
                              appSlug: app.slug,
                              visible: next,
                            })
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Change an existing staff member's roles (CEO 2026-10-21 — e.g. give the
 *  Marketing Manager the Marketing role). PATCH /users/:id/roles, audited;
 *  takes effect at their next sign-in. */
function RolesEditor({ userId, roles }: { userId: string; roles: string[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // Choices (Sales / Production / …), saved as the server roles they grant
  // (Stephanie 2026-10-26); any role this screen doesn't offer is kept.
  const [draft, setDraft] = useState<string[]>(roleChoicesOf(roles));
  const save = useMutation({
    mutationFn: () =>
      api(`/users/${userId}/roles`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roles: [...rolesFromChoices(draft), ...roles.filter((r) => !ROLE_CHOICES.some((c) => c.grants.includes(r)))] }),
      }),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["admin", "app-access"] });
    },
  });
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setDraft(roleChoicesOf(roles)); setOpen(true); }}
        className="mt-1 text-[11px] font-semibold text-ink underline underline-offset-2 hover:text-brown-500"
      >
        Edit roles
      </button>
    );
  }
  const err = save.error instanceof ApiError ? ((save.error.body as { detail?: string } | null)?.detail ?? `Couldn't save (${save.error.status}).`) : save.error ? "Couldn't save." : null;
  return (
    <div className="mt-2 w-64 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {ROLE_CHOICES.map((role) => {
          const active = draft.includes(role.key);
          return (
            <button
              type="button"
              key={role.key}
              title={role.hint}
              aria-pressed={active}
              onClick={() => setDraft((d) => (d.includes(role.key) ? d.filter((r) => r !== role.key) : [...d, role.key]))}
              className={cn(
                "rounded border px-2 py-1 text-[11px] font-semibold",
                active ? "border-ink bg-ink text-white" : "border-brown-100 bg-white text-brown-400 hover:text-brown-500",
              )}
            >
              {role.label}
            </button>
          );
        })}
      </div>
      {err && <p className="text-[11px] text-rust-600" role="alert">{err}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || draft.length === 0}>
          {save.isPending ? "Saving…" : "Save roles"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function InviteTeammate() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [title, setTitle] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const toggleRole = (value: string) => {
    setRoles((prev) =>
      prev.includes(value)
        ? prev.filter((r) => r !== value)
        : [...prev, value],
    );
  };

  const invite = useMutation({
    mutationFn: async () =>
      api<{ user_id: string; invitation_id: string }>("/users/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          full_name: fullName.trim(),
          roles: rolesFromChoices(roles), // Sales → client_rep + order_rep (Stephanie 2026-10-26)
          title: title.trim() || null,
        }),
      }),
    onSuccess: () => {
      setSentTo(email.trim());
      setError(null);
      setEmail("");
      setFullName("");
      setTitle("");
      setRoles([]);
      // Invited users are inactive until they accept, so they don't appear
      // in the matrix yet — invalidate anyway so the list is fresh.
      void queryClient.invalidateQueries({ queryKey: ["admin", "app-access"] });
    },
    onError: (err) => {
      setSentTo(null);
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError(
            "Someone already has an active account with that email. If their earlier invite expired, just re-invite them — it works.",
          );
        } else if (err.status === 400) {
          setError("Check the email, name, and that at least one role is picked.");
        } else {
          setError(`Couldn't send the invite (${err.status}). Try again.`);
        }
      } else {
        setError("Couldn't send the invite. Try again.");
      }
    },
  });

  const canSubmit =
    email.trim().length > 0 &&
    fullName.trim().length > 0 &&
    roles.length > 0 &&
    !invite.isPending;

  return (
    <div className="rounded-lg border border-brown-100 bg-white p-5">
      <h2 className="font-display text-lg font-bold text-brown-500">
        Invite a teammate
      </h2>
      <p className="mt-1 text-sm text-brown-400">
        They'll get an email to set their own password and activate their
        account. The link expires in 24 hours — if it does, just re-invite
        them (re-inviting an expired invite works, and refreshes their roles).
      </p>

      <form
        className="mt-4 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          invite.mutate();
        }}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label
              htmlFor="invite-email"
              className="mb-1.5 block text-sm font-semibold text-brown-500"
            >
              Email
            </label>
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@withother.com"
            />
          </div>
          <div>
            <label
              htmlFor="invite-name"
              className="mb-1.5 block text-sm font-semibold text-brown-500"
            >
              Full name
            </label>
            <Input
              id="invite-name"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label
              htmlFor="invite-title"
              className="mb-1.5 block text-sm font-semibold text-brown-500"
            >
              Title <span className="font-normal text-brown-300">(optional)</span>
            </label>
            <Input
              id="invite-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Account Executive"
            />
          </div>
        </div>

        <div>
          <span className="mb-2 block text-sm font-semibold text-brown-500">
            Roles
          </span>
          <div className="flex flex-wrap gap-2">
            {ROLE_CHOICES.map((role) => {
              const active = roles.includes(role.key);
              return (
                <button
                  type="button"
                  key={role.key}
                  onClick={() => toggleRole(role.key)}
                  title={role.hint}
                  aria-pressed={active}
                  className={cn(
                    "rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors",
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-brown-100 bg-white text-brown-400 hover:border-brown-200 hover:text-brown-500",
                  )}
                >
                  {role.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600"
          >
            {error}
          </div>
        )}
        {sentTo && !error && (
          <div
            role="status"
            className="rounded-md border border-peach-400 bg-peach-50 px-3 py-2 text-sm text-brown-500"
          >
            Invitation sent to {sentTo} — expires in 24 hours.
          </div>
        )}

        <Button type="submit" variant="destructive" disabled={!canSubmit}>
          {invite.isPending ? "Sending…" : "Send invitation"}
        </Button>
      </form>
    </div>
  );
}

function CellToggle(props: {
  cell: MatrixCell;
  disabled: boolean;
  onChange: (next: boolean | null) => void;
}) {
  const { cell, disabled, onChange } = props;

  // 3-state cycle on click: default → show → hide → default …
  const cycle = (): boolean | null => {
    if (cell.override === null) return true;
    if (cell.override === true) return false;
    return null;
  };

  let icon: React.ReactNode;
  let cls: string;
  let title: string;
  if (cell.override === null && cell.defaultVisible) {
    // Their roles already show this — a light tick so it reads as checked
    // (Stephanie 2026-10-26: "the boxes for those roles should be checked").
    icon = <Check className="h-3.5 w-3.5" aria-hidden />;
    cls =
      "border border-brown-200 bg-cream-100 text-brown-400 hover:border-brown-300 hover:text-brown-500";
    title = "Shown by their roles";
  } else if (cell.override === null) {
    icon = <Minus className="h-3.5 w-3.5" aria-hidden />;
    cls =
      "border border-brown-100 text-brown-300 hover:border-brown-200 hover:text-brown-400";
    title = "Hidden by their roles";
  } else if (cell.override === true) {
    icon = <Check className="h-3.5 w-3.5" aria-hidden />;
    cls = "bg-peach-400 text-white hover:bg-peach-500";
    title = "Force show";
  } else {
    icon = <X className="h-3.5 w-3.5" aria-hidden />;
    cls = "bg-rust-100 text-rust-600 hover:bg-rust-200";
    title = "Force hide";
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(cycle())}
      title={title}
      aria-label={title}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-50 mx-auto",
        cls,
      )}
    >
      {icon}
    </button>
  );
}

// ─── Two-step sign-in (CEO 2026-10-21) ────────────────────────────────────
// Who has it set up; "Reset" makes that person set it up again at their next
// sign-in (lost phone and recovery codes) and signs them out everywhere. Each
// reset is written to the audit log with the admin as the actor.

interface TwoStepAdminRow {
  id: string;
  email: string;
  full_name: string | null;
  enabled: boolean;
  enabled_at: string | null;
}

interface TwoStepAdminResponse {
  mode: "required" | "optional" | "off";
  ready: boolean;
  reason: "off" | "tables_missing" | "key_missing" | "misconfigured" | null;
  users: TwoStepAdminRow[];
}

const TWO_STEP_ADMIN_KEY = ["admin", "mfa"] as const;

function TwoStepAdmin() {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: TWO_STEP_ADMIN_KEY,
    queryFn: () => api<TwoStepAdminResponse>("/admin/mfa"),
  });
  const [confirming, setConfirming] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // Step-up: the admin types a fresh code from their OWN app to reset someone.
  const [adminCode, setAdminCode] = useState("");
  const reset = useMutation({
    mutationFn: (userId: string) =>
      api(`/admin/users/${userId}/mfa-reset`, { method: "POST", body: JSON.stringify({ code: adminCode.trim() || undefined }) }),
    onSuccess: (_d, userId) => {
      const who = q.data?.users.find((u) => u.id === userId);
      setMsg(
        `Reset done. ${who?.full_name ?? who?.email ?? "They"} will set up two-step sign-in again at their next sign-in.`,
      );
      setConfirming(null);
      setAdminCode("");
      void queryClient.invalidateQueries({ queryKey: TWO_STEP_ADMIN_KEY });
    },
    onError: (err) => {
      setAdminCode("");
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      setMsg(
        code === "invalid_code"
          ? "That code didn't work. Type the 6-digit code from YOUR authenticator app."
          : code === "mfa_not_enrolled"
            ? "Set up two-step sign-in for yourself first (Settings), then you can reset others."
            : code === "too_many_attempts"
              ? "Too many wrong codes. Wait 15 minutes and try again."
              : "Couldn't reset two-step sign-in. Try again in a moment.",
      );
    },
  });

  const data = q.data;
  const tablesThere = !!data && data.reason !== "tables_missing";
  return (
    <section className="rounded-lg border border-brown-100 bg-white p-6">
      <h2 className="font-display text-lg font-bold text-brown-500">
        Two-step sign-in
      </h2>
      <p className="mt-1 max-w-3xl text-[15px] text-brown-400">
        {!data
          ? "Every staff member signs in with a password plus a 6-digit code from their phone."
          : !data.ready
            ? data.reason === "misconfigured"
              ? "Two-step sign-in is MISCONFIGURED — staff sign-in is refused. Check MFA_ENCRYPTION_KEY and the database step, or set STAFF_2FA=off (break-glass)."
              : data.reason === "off"
              ? "Two-step sign-in is switched off (STAFF_2FA=off)."
              : "Two-step sign-in is almost ready — it switches on once the database step has run and the encryption key is set."
            : data.mode === "required"
              ? "Required for everyone: anyone not set up yet is asked to set it up at their next sign-in."
              : "Optional: people can turn it on in Settings."}{" "}
        If someone loses their phone and their recovery codes, reset it here —
        they set it up again at their next sign-in.
      </p>
      {q.isLoading && <p className="mt-4 text-sm text-brown-300">Loading…</p>}
      {q.isError && (
        <p className="mt-4 text-sm text-rust-600">
          Could not load two-step sign-in status.
        </p>
      )}
      {msg && (
        <div
          role="status"
          className="mt-4 rounded-md border border-peach-400 bg-peach-50 px-4 py-2 text-[15px] text-brown-500"
        >
          {msg}
        </div>
      )}
      {data && (
        <table className="mt-4 w-full text-[15px]">
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-b border-brown-50 last:border-0">
                <td className="py-3 pr-4">
                  <div className="font-semibold text-brown-500">
                    {u.full_name ?? u.email}
                  </div>
                  <div className="text-[13px] text-brown-300">{u.email}</div>
                </td>
                <td className="py-3 pr-4 text-brown-400">
                  {u.enabled && u.enabled_at
                    ? `On since ${new Date(u.enabled_at).toLocaleDateString()}`
                    : "Not set up"}
                </td>
                <td className="py-3 text-right">
                  {confirming === u.id ? (
                    <span className="inline-flex flex-wrap items-center justify-end gap-2">
                      <span className="text-[14px] text-brown-400">
                        Reset and sign them out? Your code:
                      </span>
                      {data.mode !== "off" && (
                        <Input
                          aria-label="Your 6-digit code"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={7}
                          value={adminCode}
                          onChange={(e) => setAdminCode(e.target.value)}
                          placeholder="123456"
                          className="h-8 w-28"
                        />
                      )}
                      <Button
                        size="sm"
                        variant="risk"
                        disabled={reset.isPending || (data.mode !== "off" && adminCode.trim().length < 6)}
                        onClick={() => reset.mutate(u.id)}
                      >
                        {reset.isPending ? "Resetting…" : "Yes, reset"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={reset.isPending}
                        onClick={() => {
                          setConfirming(null);
                          setAdminCode("");
                        }}
                      >
                        Cancel
                      </Button>
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!tablesThere}
                      onClick={() => {
                        setMsg(null);
                        setConfirming(u.id);
                      }}
                    >
                      Reset
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
